import { all, put, call, select, takeLatest } from 'redux-saga/effects'
import { push } from 'connected-react-router'

import {
  getInteraction,
  getInteractionEvents,
  sendInteractionMessage,
  closeTicket as closeTicketRequest,
  sendFeedback as sendFeedbackRequest,
  sendDisagreement as sendDisagreementRequest,
  rejectWorkRequest as sendRejectWorkRequest,
  reopenTicket as reopenTicketRequest,
  approveWorkRequest as sendApproveWorkRequest
} from '../api'
import { apiError, showPopup } from '../helpers'
import { captureException } from '../helpers/Sentry'
import { logout, LOGOUT } from './auth'
import { selectActiveState, fetchStates } from './states'

const initialState = {
  data: {},
  events: [],
  selectedInteractionId: '',
  severity: 0,
  files: [],
  messageText: '',
  error: null,
  loading: false,
  changingSeverity: false,
  forceDisableCloseButton: false
}

export const moduleName = 'interaction'
export const FETCH_INTERACTION_REQUEST = `${moduleName}/FETCH_INTERACTION_REQUEST`
export const FETCH_INTERACTION_SUCCESS = `${moduleName}/FETCH_INTERACTION_SUCCESS`
export const FETCH_INTERACTION_FAIL = `${moduleName}/FETCH_INTERACTION_FAIL`

export const CLOSE_TICKET_REQUEST = `${moduleName}/CLOSE_TICKET_REQUEST`
export const CLOSE_TICKET_SUCCESS = `${moduleName}/CLOSE_TICKET_SUCCESS`
export const CLOSE_TICKET_FAIL = `${moduleName}/CLOSE_TICKET_FAIL`

export const APPROVE_WORK_REQUEST_REQUEST = `${moduleName}/APPROVE_WORK_REQUEST_REQUEST`
export const APPROVE_WORK_REQUEST_SUCCESS = `${moduleName}/APPROVE_WORK_REQUEST_SUCCESS`
export const APPROVE_WORK_REQUEST_FAIL = `${moduleName}/APPROVE_WORK_REQUEST_FAIL`

export const REJECT_WORK_REQUEST_REQUEST = `${moduleName}/REJECT_WORK_REQUEST_REQUEST`
export const REJECT_WORK_REQUEST_SUCCESS = `${moduleName}/REJECT_WORK_REQUEST_SUCCESS`
export const REJECT_WORK_REQUEST_FAIL = `${moduleName}/REJECT_WORK_REQUEST_FAIL`

export const SEND_FEEDBACK_REQUEST = `${moduleName}/SEND_FEEDBACK_REQUEST`
export const SEND_FEEDBACK_SUCCESS = `${moduleName}/SEND_FEEDBACK_SUCCESS`
export const SEND_FEEDBACK_FAIL = `${moduleName}/SEND_FEEDBACK_FAIL`

export const SEND_DISAGREEMENT_REQUEST = `${moduleName}/SEND_DISAGREEMENT_REQUEST`
export const SEND_DISAGREEMENT_SUCCESS = `${moduleName}/SEND_DISAGREEMENT_SUCCESS`
export const SEND_DISAGREEMENT_FAIL = `${moduleName}/SEND_DISAGREEMENT_FAIL`

export const REOPEN_TICKET_REQUEST = `${moduleName}/REOPEN_TICKET_REQUEST`
export const REOPEN_TICKET_SUCCESS = `${moduleName}/REOPEN_TICKET_SUCCESS`
export const REOPEN_TICKET_FAIL = `${moduleName}/REOPEN_TICKET_FAIL`

export const CLEAR_INTERACTION_STORE = `${moduleName}/CLEAR_INTERACTION_STORE`

export const SEND_INTERACTION_MESSAGE_REQUEST = `${moduleName}/SEND_INTERACTION_MESSAGE_REQUEST`
export const SEND_INTERACTION_MESSAGE_SUCCESS = `${moduleName}/SEND_INTERACTION_MESSAGE_SUCCESS`
export const SEND_INTERACTION_MESSAGE_FAIL = `${moduleName}/SEND_INTERACTION_MESSAGE_FAIL`

export const CHANGE_SEVERITY_REQUEST = `${moduleName}/CHANGE_SEVERITY_REQUEST`
export const CHANGE_SEVERITY_SUCCESS = `${moduleName}/CHANGE_SEVERITY_SUCCESS`
export const CHANGE_SEVERITY_FAIL = `${moduleName}/CHANGE_SEVERITY_FAIL`

export const SET_SEVERITY = `${moduleName}/SET_SEVERITY`

export const UPDATE_MESSAGE = `${moduleName}/UPDATE_MESSAGE`
export const ATTACH_FILES = `${moduleName}/ATTACH_FILES`
export const REMOVE_FILE = `${moduleName}/REMOVE_FILE`
export const FORCE_DISABLE_BUTTON = `${moduleName}/FORCE_DISABLE_BUTTON`

export const SHOW_WARNING_FILE_SIZE = `${moduleName}/SHOW_WARNING_FILE_SIZE`

export default function reducer(state = initialState, action) {
  const { type, payload } = action

  switch (type) {
    case FETCH_INTERACTION_REQUEST:
      return { ...state, loading: true, selectedInteractionId: payload }

    case FETCH_INTERACTION_SUCCESS:
    case SEND_INTERACTION_MESSAGE_SUCCESS:
    case CLOSE_TICKET_SUCCESS:
    case SEND_FEEDBACK_SUCCESS:
    case SEND_DISAGREEMENT_SUCCESS:
    case REOPEN_TICKET_SUCCESS:
    case APPROVE_WORK_REQUEST_SUCCESS:
    case REJECT_WORK_REQUEST_SUCCESS:
      return {
        ...state,
        ...payload,
        loading: false,
        files: [],
        messageText: '',
        forceDisableCloseButton: false
      }

    case CHANGE_SEVERITY_REQUEST: {
      return {
        ...state,
        loading: false
      }
    }

    case CHANGE_SEVERITY_SUCCESS: {
      return { ...state, ...payload }
    }

    case CLEAR_INTERACTION_STORE:
      return { ...initialState }
    case SET_SEVERITY:
      return { ...state, severity: payload }

    case FORCE_DISABLE_BUTTON:
      return { ...state, forceDisableCloseButton: true }

    case SEND_INTERACTION_MESSAGE_REQUEST:
    case CLOSE_TICKET_REQUEST:
    case SEND_FEEDBACK_REQUEST:
    case SEND_DISAGREEMENT_REQUEST:
    case CHANGE_SEVERITY_REQUEST:
    case REOPEN_TICKET_REQUEST:
    case APPROVE_WORK_REQUEST_REQUEST:
    case REJECT_WORK_REQUEST_REQUEST:
      return { ...state, loading: true }

    case FETCH_INTERACTION_FAIL:
    case SEND_INTERACTION_MESSAGE_FAIL:
    case CLOSE_TICKET_FAIL:
    case SEND_FEEDBACK_FAIL:
    case SEND_DISAGREEMENT_FAIL:
    case REOPEN_TICKET_FAIL:
    case APPROVE_WORK_REQUEST_FAIL:
    case REJECT_WORK_REQUEST_FAIL:
      return { ...state, loading: false, error: payload }

    case ATTACH_FILES:
      return { ...state, files: [...state.files, ...payload] }
    case REMOVE_FILE: {
      const files = [
        ...state.files.slice(0, payload),
        ...state.files.slice(payload + 1)
      ]

      return { ...state, files }
    }
    case UPDATE_MESSAGE:
      return { ...state, messageText: payload }

    case LOGOUT:
      return { ...initialState }

    default:
      return state
  }
}

export function fetchInteraction(id, updateState = false) {
  return {
    type: FETCH_INTERACTION_REQUEST,
    payload: id,
    updateState
  }
}

export function sendMessage() {
  return {
    type: SEND_INTERACTION_MESSAGE_REQUEST
  }
}

export function attachFiles(files) {
  return {
    type: ATTACH_FILES,
    payload: files
  }
}

export function removeAttachment(id) {
  return {
    type: REMOVE_FILE,
    payload: id
  }
}

export function inputMessageHandler(value) {
  return {
    type: UPDATE_MESSAGE,
    payload: value
  }
}

export function setSeverity(value) {
  return {
    type: SET_SEVERITY,
    payload: value
  }
}

export function clearInteractionStore() {
  return {
    type: CLEAR_INTERACTION_STORE
  }
}

export function closeTicket(data) {
  return {
    type: CLOSE_TICKET_REQUEST,
    payload: data
  }
}

export function sendFeedback(data) {
  return {
    type: SEND_FEEDBACK_REQUEST,
    payload: data
  }
}

export function sendDisagreement(data) {
  return {
    type: SEND_DISAGREEMENT_REQUEST,
    payload: data
  }
}

export function reopenTicket(data) {
  return {
    type: REOPEN_TICKET_REQUEST,
    payload: data
  }
}

export function changeSeverity(payload) {
  return {
    type: CHANGE_SEVERITY_REQUEST,
    payload
  }
}

export function showWarningFileSize() {
  return {
    type: SHOW_WARNING_FILE_SIZE
  }
}

export function approveWorkRequest() {
  return {
    type: APPROVE_WORK_REQUEST_REQUEST
  }
}

export function rejectWorkRequest(payload) {
  return {
    type: REJECT_WORK_REQUEST_REQUEST,
    payload
  }
}

const getOrFetchInteractionSaga = function*(id) {
  const interactions = yield select((state) => state.interactions.entities)
  let interaction = interactions.filter((i) => i.interaction_id === id)[0]

  if (interaction) {
    return interaction
  } else {
    interaction = yield call(getInteraction, id)

    return interaction
  }
}

export const fetchInteractionSaga = function*({ payload, updateState }) {
  try {
    const id = payload
    yield put(push(`/dashboard/interactions/${id}`))

    const { loading, initialization } = yield select(
      ({ interactions }) => interactions
    )

    if (loading || initialization) {
      return null
    }

    const [data, events] = yield all([
      call(getOrFetchInteractionSaga, id),
      call(getInteractionEvents, id)
    ])
    const { severity } = data

    yield put({
      type: FETCH_INTERACTION_SUCCESS,
      payload: { data, events, severity }
    })

    const stateStore = yield select(({ states }) => states)

    if (updateState) {
      if (data.state === 'Closed' && stateStore.selectedState !== 'CLOSED') {
        yield put(selectActiveState(2, false, true, true))
      }
    }
  } catch (e) {
    yield put(showPopup(e))

    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: FETCH_INTERACTION_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: FETCH_INTERACTION_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const changeSeveritySaga = function*(action) {
  try {
    const { comment, severity } = action.payload
    const { selectedInteractionId } = yield select((state) => state.interaction)
    const response = yield call(sendInteractionMessage, {
      id: selectedInteractionId,
      text: comment,
      severity
    })
    const events = yield call(getInteractionEvents, selectedInteractionId)
    yield put({
      type: CHANGE_SEVERITY_SUCCESS,
      payload: { data: response, events, severity }
    })
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: CHANGE_SEVERITY_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: CHANGE_SEVERITY_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const sendInteractionMessageSaga = function*() {
  try {
    const {
      selectedInteractionId,
      // severity,
      files,
      messageText
    } = yield select((state) => state.interaction)
    const response = yield call(sendInteractionMessage, {
      id: selectedInteractionId,
      text: messageText,
      files
    })
    const events = yield call(getInteractionEvents, selectedInteractionId)
    yield put({
      type: SEND_INTERACTION_MESSAGE_SUCCESS,
      payload: { data: response, events }
    })
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: FETCH_INTERACTION_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: FETCH_INTERACTION_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const closeTicketSaga = function*(action) {
  try {
    const { selectedInteractionId } = yield select((state) => state.interaction)
    const response = yield call(closeTicketRequest, selectedInteractionId)
    yield put({
      type: CLOSE_TICKET_SUCCESS,
      payload: { data: response }
    })
    const { mark, mark2, mark3 } = action.payload

    if (mark && mark2 && mark3) {
      yield put(sendFeedback(action.payload))
    } else {
      yield put(selectActiveState(2, false))
    }
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: CLOSE_TICKET_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      } else if (code === 660) {
        yield put({
          type: FORCE_DISABLE_BUTTON
        })
      }
    } else {
      yield put({
        type: CLOSE_TICKET_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const approveWorkRequestSaga = function*(action) {
  try {
    const interaction = yield select((state) => state.interaction)
    const foundInteractionWithWR = interaction
      ? interaction.events.findLast((event) => {
        const workRequest = event.workRequest

        if (!workRequest) return false

        const isCanBeApproved = Boolean(
          !workRequest.accepted && !workRequest.cancelled && workRequest.uid
        )

        return isCanBeApproved
      })
      : null

    if (!foundInteractionWithWR) return
    const foundWR = foundInteractionWithWR.workRequest

    yield call(sendApproveWorkRequest, foundWR.uid)

    yield put({
      type: APPROVE_WORK_REQUEST_SUCCESS
    })

    yield put(selectActiveState(0, false))
    yield put(fetchStates())
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: APPROVE_WORK_REQUEST_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      } else if (code === 660) {
        yield put({
          type: FORCE_DISABLE_BUTTON
        })
      }
    } else {
      yield put({
        type: APPROVE_WORK_REQUEST_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const sendFeedbackSaga = function*(action) {
  try {
    const { selectedInteractionId } = yield select((state) => state.interaction)
    const response = yield call(
      sendFeedbackRequest,
      selectedInteractionId,
      action.payload
    )
    yield put({
      type: SEND_FEEDBACK_SUCCESS,
      payload: { data: response }
    })
    yield put(selectActiveState(2, false))
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: SEND_FEEDBACK_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: SEND_FEEDBACK_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const sendDisagreementSaga = function*(action) {
  try {
    const { selectedInteractionId } = yield select((state) => state.interaction)
    const comment = action.payload
    const response = yield call(
      sendDisagreementRequest,
      selectedInteractionId,
      comment
    )

    yield put({
      type: SEND_DISAGREEMENT_SUCCESS,
      payload: { data: response }
    })
    yield put(selectActiveState(0, false))
    yield put(fetchStates())
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: SEND_DISAGREEMENT_SUCCESS,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: SEND_DISAGREEMENT_SUCCESS,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const reopenTicketSaga = function*(action) {
  try {
    const { selectedInteractionId } = yield select((state) => state.interaction)
    const comment = action.payload
    const response = yield call(
      reopenTicketRequest,
      selectedInteractionId,
      comment
    )

    yield put({
      type: REOPEN_TICKET_SUCCESS,
      payload: { data: response }
    })
    yield put(selectActiveState(0, false))
    yield put(fetchStates())
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: REOPEN_TICKET_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: REOPEN_TICKET_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const showWarningFileSizeSaga = function*() {
  yield put(
    showPopup({
      code: 'fileSize'
    })
  )
}

export const rejectWorkRequestSaga = function*(action) {
  try {
    const interaction = yield select((state) => state.interaction)
    const foundInteractionWithWR = interaction
      ? interaction.events.findLast((event) => {
        const workRequest = event.workRequest

        if (!workRequest) return false

        const isCanBeApproved = Boolean(
          !workRequest.accepted && !workRequest.cancelled && workRequest.uid
        )

        return isCanBeApproved
      })
      : null

    if (!foundInteractionWithWR) return
    const foundWR = foundInteractionWithWR.workRequest

    const comment = action.payload
    yield call(sendRejectWorkRequest, foundWR.uid, comment)

    yield put({
      type: REJECT_WORK_REQUEST_SUCCESS
    })
    yield put(selectActiveState(0, false))
    yield put(fetchStates())
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: REJECT_WORK_REQUEST_SUCCESS,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: REJECT_WORK_REQUEST_SUCCESS,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const saga = function*() {
  yield all([
    takeLatest(FETCH_INTERACTION_REQUEST, fetchInteractionSaga),
    takeLatest(SEND_INTERACTION_MESSAGE_REQUEST, sendInteractionMessageSaga),
    takeLatest(CLOSE_TICKET_REQUEST, closeTicketSaga),
    takeLatest(SEND_FEEDBACK_REQUEST, sendFeedbackSaga),
    takeLatest(SEND_DISAGREEMENT_REQUEST, sendDisagreementSaga),
    takeLatest(REOPEN_TICKET_REQUEST, reopenTicketSaga),
    takeLatest(CHANGE_SEVERITY_REQUEST, changeSeveritySaga),
    takeLatest(SHOW_WARNING_FILE_SIZE, showWarningFileSizeSaga),
    takeLatest(APPROVE_WORK_REQUEST_REQUEST, approveWorkRequestSaga),
    takeLatest(REJECT_WORK_REQUEST_REQUEST, rejectWorkRequestSaga)
  ])
}
